// combatpro/conditions/ConditionsPanel.js
//
// Combat PRO - ConditionsPanel (Stage 43: Conditions Panel Integration).
//
// The React migration of the legacy "Active Conditions" slide-in panel
// (battletracker.html markup 13110-13118; render logic updateConditionsPanel
// 9112-9164). This stage is DISPLAY-ONLY: it aggregates and shows the active
// conditions read from Monster Runtime state and reuses the Conditions Engine
// for descriptions. It does NOT implement remove, apply, duration, EFFECTS_DB,
// or any DOM manipulation.
//
// Confirmed display decisions (per Stage 43 brief):
//   - Aggregate from monster.effect (Effects Runtime field, read only), plus the
//     vestigial monster.conditions[] array exactly as the legacy did (guarded;
//     always empty in practice - the locked factory has no conditions field).
//   - Display the RAW effect value (the canonical EFFECTS_DB name). No
//     normalisation for display (normalizeCondition would lower-case and is
//     idempotent on canonical values anyway).
//   - Show a description ONLY when conditionsEngine.getConditionDescription has
//     one (the 14 core conditions); the ~7 non-condition effects (Hasted,
//     Slowed, Blessed, Bane, Hexed, On Fire) render name-only.
//   - Remove affordance (Stage 123, P5): the tag carries the legacy onclick,
//     "Click to remove" title, and trailing "X" glyph (updateConditionsPanel
//     9147), invoking the CombatContext removeCondition runtime - the confirm,
//     the updateEffect-based clear, and the combat log are all owned there.
//   - No tooltip (showConditionTooltip is dead code in the legacy and is not
//     reproduced); descriptions render as visible inline text.
//
// Visibility is owned by the parent (CombatProEngineView local state) and passed
// in as isOpen / onClose; the legacy's dynamic top-positioning (toggleConditions
// measuring the toggle button rect) is intentionally NOT reproduced - the
// .conditions-panel CSS positions the panel and the .open class toggles it.
//
// Active-monster filtering matches the legacy: only monsters within
// activeMonsterCount with maxHP > 0 are scanned (updateConditionsPanel 9120-9125).
// Monster name uses the raw customName || selectedMonster || "Monster N" fallback
// (9140), NOT deriveTabName (no truncation), matching the legacy panel.
//
// No DOM manipulation, no getElementById, no innerHTML. No optional chaining.
// No em dash. Do not edit battletracker.html.

import React, { useContext } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { getConditionDescription } from './conditionsEngine';

function ConditionsPanel({ isOpen, onClose }) {
  const { state, removeCondition } = useContext(CombatContext);

  // Aggregate active conditions, mirroring updateConditionsPanel (9119-9153)
  // with the DOM/innerHTML build replaced by React data. Bound by both
  // activeMonsterCount and the array length (defensive; always 16 in practice).
  const entries = [];
  let limit = state.activeMonsterCount;
  if (limit > state.monsters.length) { limit = state.monsters.length; }
  for (let i = 0; i < limit; i++) {
    const monster = state.monsters[i];
    if (!monster.maxHP || monster.maxHP === 0) { continue; }

    const conditions = [];
    // From the Effects field (read only).
    if (monster.effect && monster.effect !== '' && monster.effect !== 'None') {
      conditions.push(monster.effect);
    }
    // From the vestigial conditions array, guarded exactly as the legacy (9133).
    if (monster.conditions && Array.isArray(monster.conditions)) {
      conditions.push.apply(conditions, monster.conditions);
    }

    if (conditions.length > 0) {
      const monsterName = monster.customName || monster.selectedMonster || ('Monster ' + (i + 1));
      entries.push({ index: i, monsterName: monsterName, conditions: conditions });
    }
  }

  const hasConditions = entries.length > 0;

  return (
    <div className={isOpen ? 'conditions-panel open' : 'conditions-panel'} id="conditionsPanel">
      <div className="conditions-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>🎭 Active Conditions</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--cp-gold-bright)', cursor: 'pointer', fontSize: '16px', padding: '0 4px', lineHeight: 1 }}>✕</button>
      </div>
      <div className="conditions-content" id="conditionsContent">
        {hasConditions ? (
          entries.map(entry => (
            <div className="condition-entry" key={entry.index}>
              <div className="condition-monster-name">{entry.monsterName}</div>
              <div>
                {entry.conditions.map((cond, ci) => {
                  const desc = getConditionDescription(cond);
                  return (
                    <div className="vi-condition-row" key={ci}>
                      <span
                        className="condition-tag"
                        title="Click to remove"
                        onClick={(function (mi, c) { return function () { removeCondition(mi, c); }; })(entry.index, cond)}
                      >{cond} ✕</span>
                      {desc ? <div className="vi-condition-desc">{desc}</div> : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="vi-empty">No active conditions.<br />Conditions will appear here.</div>
        )}
      </div>
    </div>
  );
}

export default ConditionsPanel;
